/**
 * ResultApprovalQueryPanel — the structured query/filter region for the Result
 * Approval (Data Review Level-3) form.
 *
 * A SEPARATE COPY of ResultEntryQueryPanel (task RTCFR, 2026-09-04): identical
 * layout / controls / behaviour, but its OWN component + types + CSS (`.rap-`)
 * so future changes to the Result Approval panel affect Result Approval ONLY and
 * never touch Result Entry Express. Do NOT re-point this at the REE component.
 *
 * Layout: a fixed 7-column × 2-row grid —
 *   col1 QC Batch ID / Job ID · col2 Sample Matrix / Test · col3 Project ID /
 *   Project Name · col4 Client / Site Name · col5 Date Analyzed F/T ·
 *   col6 Date Received F/T · col7 Date Collected F/T. Each date column keeps a
 *   SINGLE label on its From cell; the To cell sits directly below.
 * Lookup filters are CHECKED COMBOS (multi-select) with a header select-all;
 * values are sent '|'-joined and matched server-side (IN via STRING_SPLIT), the
 * options coming from AI_Analysis_ResultApproval_FilterOptions_SP.
 */
import { useMemo, useState } from 'react';
import { LookupCombo } from '../../components/LookupCombo';
import './resultApprovalQueryPanel.css';

/** A QC Batch option — value + the two display-only columns (batch Test + the
 *  Employee who created it) shown in the 3-column picker. */
export interface RapprBatchOption { id: string; test: string; method: string; emp?: string }

/** The query-panel filter values. Empty array/'' = "All" (no filter). */
export interface RapprFilters {
  batchId:        string[];   // QC Batch ID (= QCBatchID)
  id:             string[];   // Job ID
  test:           string[];
  matrix:         string[];   // Sample Matrix
  clientName:     string[];
  projectId:      string[];
  projectName:    string[];
  sampleLocation: string[];   // Site Name (SampleLogIn.StationLocationName)
  receivedFrom:   string;
  receivedTo:     string;
  analyzedFrom:   string;
  analyzedTo:     string;
  // Present-but-disabled ("SOON") until its column is wired:
  collectedFrom:  string;
  collectedTo:    string;
}

export const EMPTY_RAPPR_FILTERS: RapprFilters = {
  batchId: [], id: [], test: [], matrix: [], clientName: [], projectId: [],
  projectName: [], sampleLocation: [],
  receivedFrom: '', receivedTo: '', analyzedFrom: '', analyzedTo: '',
  collectedFrom: '', collectedTo: '',
};

/** Distinct dropdown options for the panel. */
export interface RapprOptions {
  clients:         string[];
  projects:        string[];
  projectNames:    string[];
  tests:           string[];
  matrices:        string[];
  jobIds:          string[];
  sampleLocations: string[];
  batchIds:        RapprBatchOption[];   // QC Batch ID · Test · Method
  /** Per-value pending-sample counts (user request 2026-09-09), keyed by filter field.
   *  When a field's map is present its combo renders a 2-column "<Label> · Sample Count"
   *  picker (RV = waiting for validation, RA = pending approval). */
  counts?: Partial<Record<'test' | 'matrix' | 'projectId' | 'projectName' | 'clientName' | 'sampleLocation', Record<string, number>>>;
  /** Min/max selectable date per date pair (yyyy-MM-dd) for the current filter set
   *  (user request 2026-09-09) — when present the date pickers are constrained to that
   *  range; callers that omit it keep unbounded pickers. */
  dateBounds?: Partial<Record<'analyzed' | 'received' | 'collected', { min?: string; max?: string }>>;
}

export const EMPTY_RAPPR_OPTIONS: RapprOptions = {
  clients: [], projects: [], projectNames: [], tests: [], matrices: [],
  jobIds: [], sampleLocations: [], batchIds: [],
};

/** True when any filter has a value. */
export function hasActiveRapprFilters(f: RapprFilters): boolean {
  return !!(f.batchId.length || f.id.length || f.test.length || f.matrix.length
    || f.clientName.length || f.projectId.length || f.projectName.length
    || f.sampleLocation.length || f.receivedFrom || f.receivedTo
    || f.analyzedFrom || f.analyzedTo || f.collectedFrom || f.collectedTo);
}

const toLookup = (col: string, values: string[]) => ({ cols: [col], rows: values.map(v => [v]) });

/** Popup width sized to the longest option text. */
const popW = (values: string[], header: string) => {
  const longest = values.reduce((m, v) => Math.max(m, v.length), header.length);
  return Math.min(380, Math.max(180, Math.round(longest * 7 + 78)));
};

interface Props {
  filters: RapprFilters;
  onChange: (patch: Partial<RapprFilters>) => void;
  options: RapprOptions;
  loadingOptions?: boolean;
  applying?: boolean;
  onApply: () => void;
  onClear: () => void;
  /** Controlled collapse state — when provided the parent owns open/close. */
  collapsed?: boolean;
  onCollapsedChange?: (c: boolean) => void;
  /** Pending-entry counts shown inside the panel's bottom bar. */
  pendingBatches?: number;
  pendingJobs?: number;
  pendingLoading?: boolean;
  /** Label for the pending-summary bar. Defaults to "Pending Result Approval";
   *  Result Validation passes "Pending Result Validation" (task RTCFR). */
  pendingLabel?: string;
  /** Which date-pair columns render as "soon" (disabled, greyed) because the
   *  caller's data doesn't carry that granularity yet. Defaults to REE's own
   *  behaviour (only Date Collected is deferred). */
  dateSoon?: { analyzed?: boolean; received?: boolean; collected?: boolean };
}

export function ResultApprovalQueryPanel({
  filters, onChange, options,
  applying: _applying = false, onApply: _onApply, onClear: _onClear,
  collapsed: collapsedProp,
  pendingBatches, pendingJobs, pendingLoading = false,
  pendingLabel = 'Pending Result Approval',
  dateSoon,
}: Props) {
  const soonAnalyzed  = dateSoon?.analyzed  ?? false;
  const soonReceived  = dateSoon?.received  ?? false;
  const soonCollected = dateSoon?.collected ?? true;
  const [collapsedInternal] = useState(false);
  const collapsed = collapsedProp !== undefined ? collapsedProp : collapsedInternal;

  const recvError = useMemo(() =>
    filters.receivedFrom && filters.receivedTo && filters.receivedFrom > filters.receivedTo
      ? 'Date Received “From” must be on or before “To”.' : null,
    [filters.receivedFrom, filters.receivedTo]);
  const anaError = useMemo(() =>
    filters.analyzedFrom && filters.analyzedTo && filters.analyzedFrom > filters.analyzedTo
      ? 'Date Analyzed “From” must be on or before “To”.' : null,
    [filters.analyzedFrom, filters.analyzedTo]);
  const colError = useMemo(() =>
    filters.collectedFrom && filters.collectedTo && filters.collectedFrom > filters.collectedTo
      ? 'Date Collected “From” must be on or before “To”.' : null,
    [filters.collectedFrom, filters.collectedTo]);
  const dateError = recvError ?? anaError ?? colError;

  /** One single-column checked-combo field. */
  const combo = (label: string, values: string[], opts: string[], onToggle: (n: string[]) => void) => (
    <Field label={label}>
      <LookupCombo className="rap-lk" multi multiActions showFilter placeholder="All"
        popupClassName="rap-pick" popupWidth={popW(opts, label)}
        lookup={toLookup(label, opts)} values={values} onToggle={onToggle} />
    </Field>
  );

  /** A checked-combo that adds a "Sample Count" column when `counts` is supplied
   *  (user request 2026-09-09); otherwise the single-column combo. The option value is
   *  still the first column, so selection/cascade/Clear behave exactly as before. */
  const comboC = (label: string, values: string[], opts: string[],
                  counts: Record<string, number> | undefined, onToggle: (n: string[]) => void) => {
    if (!counts) return combo(label, values, opts, onToggle);
    const nameW = Math.min(300, Math.max(150, popW(opts, label) - 78 + 24));
    return (
      <Field label={label}>
        <LookupCombo className="rap-lk" multi multiActions showFilter placeholder="All"
          popupClassName="rap-pick" popupWidth={nameW + 100 + 28} colWidths={[nameW, 90]}
          lookup={{ cols: [label, 'Sample Count'], rows: opts.map(o => [o, String(counts[o] ?? 0)]) }}
          values={values} onToggle={onToggle} />
      </Field>
    );
  };

  /** One date input cell (From on row 1, or To on row 2 with an empty label).
   *  RTCFR (2026-09-17, Option B2): while the box is EMPTY we overlay a grey HINT showing the
   *  available date span for the current filter combination — the From cell hints the earliest
   *  (min) date, the To cell the latest (max) — with the full range in the tooltip. The hint is
   *  non-filtering (pointer-events:none): it only tells the user what range is selectable; the
   *  actual filter applies solely when they pick a date. Native "mm/dd/yyyy" is already hidden
   *  for empty inputs (see .rap-date[data-empty]). */
  const dateCell = (label: string, aria: string, value: string,
                    onVal: (v: string) => void, opts?: { soon?: boolean; error?: boolean; min?: string; max?: string }) => {
    const fmt = (ymd?: string) => {
      if (!ymd) return '';
      const p = ymd.slice(0, 10).split('-');
      return p.length === 3 ? `${p[1]}/${p[2]}/${p[0]}` : '';
    };
    const isTo = /-To$/.test(label);
    const hint = (!value && !opts?.soon) ? fmt(isTo ? opts?.max : opts?.min) : '';
    const range = (opts?.min || opts?.max)
      ? `Available range: ${fmt(opts?.min) || '…'} – ${fmt(opts?.max) || '…'}`
      : undefined;
    return (
      <Field label={label} soon={opts?.soon} className={opts?.error ? 'rap-field-err' : undefined}>
        <span className="rap-date-wrap">
          <input type="date" className="rap-date" aria-label={aria} disabled={opts?.soon}
            data-empty={value ? undefined : 'true'}
            min={opts?.min} max={opts?.max}
            title={opts?.soon ? 'Coming soon — not yet available in this build.' : range}
            value={value} onChange={(e) => onVal(e.target.value)} />
          {hint && <span className="rap-date-hint" aria-hidden="true">{hint}</span>}
        </span>
      </Field>
    );
  };

  // When collapsed, the outer control bar's Query toggle is the only affordance —
  // the panel disappears entirely (no title band left behind).
  if (collapsed) return null;

  return (
    <section className="rap-qp" aria-label="Query Panel">
      {/* 7-column × 2-row grid — row-major fill: children 1-7 = row 1, 8-14 = row 2. */}
      <div className="rap-qp-grid">
        {/* ── Row 1 ── */}
        <Field label="QC Batch ID">
          {/* 3-column picker: QC Batch ID · Test · Method (user request 2026-09-09,
              replacing the Employee column). Test/Method correspond to the batch. */}
          <LookupCombo className="rap-lk" multi multiActions showFilter placeholder="All"
            autoFocus
            popupClassName="rap-pick" popupWidth={440} colWidths={[150, 150, 120]}
            lookup={{ cols: ['QC Batch ID', 'Test', 'Method'],
                      rows: options.batchIds.map(b => [b.id, b.test, b.method]) }}
            values={filters.batchId} onToggle={(n) => onChange({ batchId: n })} />
        </Field>
        {comboC('Sample Matrix', filters.matrix,     options.matrices, options.counts?.matrix,     (n) => onChange({ matrix: n }))}
        {comboC('Project ID',    filters.projectId,  options.projects, options.counts?.projectId,  (n) => onChange({ projectId: n }))}
        {comboC('Client',        filters.clientName, options.clients,  options.counts?.clientName, (n) => onChange({ clientName: n }))}
        {dateCell('Date Analyzed-From',  'Date Analyzed from',  filters.analyzedFrom,  (v) => onChange({ analyzedFrom: v }),  { error: !!anaError, soon: soonAnalyzed, min: options.dateBounds?.analyzed?.min, max: options.dateBounds?.analyzed?.max })}
        {dateCell('Date Received-From',  'Date Received from',  filters.receivedFrom,  (v) => onChange({ receivedFrom: v }),  { error: !!recvError, soon: soonReceived, min: options.dateBounds?.received?.min, max: options.dateBounds?.received?.max })}
        {dateCell('Date Collected-From', 'Date Collected from', filters.collectedFrom, (v) => onChange({ collectedFrom: v }), { error: !!colError, soon: soonCollected, min: options.dateBounds?.collected?.min, max: options.dateBounds?.collected?.max })}

        {/* ── Row 2 ── */}
        {combo('Job ID',          filters.id,             options.jobIds,          (n) => onChange({ id: n }))}
        {comboC('Test',         filters.test,          options.tests,           options.counts?.test,          (n) => onChange({ test: n }))}
        {comboC('Project Name', filters.projectName,   options.projectNames,    options.counts?.projectName,   (n) => onChange({ projectName: n }))}
        {comboC('Site Name',    filters.sampleLocation, options.sampleLocations, options.counts?.sampleLocation, (n) => onChange({ sampleLocation: n }))}
        {dateCell('Date Analyzed-To',  'Date Analyzed to',  filters.analyzedTo,  (v) => onChange({ analyzedTo: v }),  { error: !!anaError, soon: soonAnalyzed, min: options.dateBounds?.analyzed?.min, max: options.dateBounds?.analyzed?.max })}
        {dateCell('Date Received-To',  'Date Received to',  filters.receivedTo,  (v) => onChange({ receivedTo: v }),  { error: !!recvError, soon: soonReceived, min: options.dateBounds?.received?.min, max: options.dateBounds?.received?.max })}
        {dateCell('Date Collected-To', 'Date Collected to', filters.collectedTo, (v) => onChange({ collectedTo: v }), { error: !!colError, soon: soonCollected, min: options.dateBounds?.collected?.min, max: options.dateBounds?.collected?.max })}
      </div>
      {dateError && <div className="rap-inline-err rap-inline-err-row" role="alert">{dateError}</div>}

      {/* Pending summary — the REE-style bottom bar (user request 2026-09-04):
          "Pending Result Approval · JobID (count) · QCBatchID (count)". JobID first,
          then QCBatchID, matching the requested order. */}
      {(pendingBatches !== undefined || pendingJobs !== undefined) && (
        <div className="rap-qp-actions">
          <div className="rap-qp-pending">
            <i className="ti ti-clock-hour-4" aria-hidden="true" />
            <span className="rap-qp-pending-label">{pendingLabel}</span>
            <span className="rap-qp-pending-stat">
              <span className="rap-qp-pending-name">JobID</span>
              <span className={`rap-qp-pending-count${pendingLoading ? ' rap-qp-pending-loading' : (pendingJobs ?? 0) === 0 ? ' rap-qp-pending-zero' : ''}`}>
                {pendingLoading ? '…' : (pendingJobs ?? 0)}
              </span>
            </span>
            <span className="rap-qp-pending-stat">
              <span className="rap-qp-pending-name">QCBatchID</span>
              <span className={`rap-qp-pending-count${pendingLoading ? ' rap-qp-pending-loading' : (pendingBatches ?? 0) === 0 ? ' rap-qp-pending-zero' : ''}`}>
                {pendingLoading ? '…' : (pendingBatches ?? 0)}
              </span>
            </span>
          </div>
        </div>
      )}
    </section>
  );
}

/** One compact label/field cell. An empty `label` renders a spacer so the row-2
 *  date "To" inputs align with the labelled controls beside them. */
function Field({ label, soon, className, children }: {
  label: string; soon?: boolean; className?: string; children: React.ReactNode;
}) {
  return (
    <div className={`rap-field${className ? ` ${className}` : ''}`}>
      <label className="rap-lbl">
        <span title={label || undefined}>{label || ' '}</span>
        {soon && <span className="rap-soon" title="Coming soon — not yet available in this build.">soon</span>}
      </label>
      <div className="rap-ctrl">{children}</div>
    </div>
  );
}
